import {
    AfterViewChecked,
    Component,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { CmpsDialog, CmpsOverlayTriggerDirective } from '@cmps/ng-uikit';
import { Store } from 'redux';
import { Subject, Subscription, timer } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';
import { createTab } from '../human-insight/human-insight.model';
import {
    DragDropEvent,
    RendererOptions,
    Tab,
    TabEvent,
    TabNode,
} from '../insight/experimental.tabset';
import {
    ConfigureTabReceiver,
    TabContext,
    TabType,
} from '../insight/tabset.model';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';
import { SearchUserContainerComponent } from '../user/search-user.container.component';
import { User } from '../user/user.model';

import {
    rootInit,
    tabsetAddTab,
    tabsetCloseTab,
    tabsetRenameTab,
    tabsetReorderTab,
    tabsetSetTabStateAction,
    tabsetSwitchTab,
} from './insight-explorer.action';
import { InsightTabConfig } from './insight-explorer.model';
import { RootState, storeStateMapper } from './insight-explorer.state';
import { TabsetComponent } from './tabset.component';

@Component({
    selector: '[cmpsInsightExplorerWidget]',
    templateUrl: './widget.ie.component.html',
})
@TakeUntilDestroy()
export class WidgetInsightExplorerComponent
    implements
        OnInit,
        AfterViewChecked,
        OnDestroy,
        StateToProps<RootState>,
        ConfigureTabReceiver<InsightTabConfig>
{
    @ViewChild('tabset') private readonly _tabset: TabsetComponent;

    TabType = TabType;

    private _storeSub: Subscription;
    state: RootState;
    opts: RendererOptions;
    private readonly _subj: Subject<
        TabEvent | DragDropEvent | { event: MouseEvent; tab: TabNode }
    >;

    tabsetEventsSub: Subscription;
    selectedTab: Tab;
    adjacentTabId: string;
    persistPeriod: boolean;

    /** @private */
    _currentFocusedTab: TabNode;

    private _clicks: number;
    private _clicksTimerSub: Subscription;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _viewContainerRef: ViewContainerRef,
        @Inject(STORE) private readonly _store: Store,
        private readonly _dialog: CmpsDialog,
    ) {
        this._subj = new Subject();
        this._subj
            .asObservable()
            .pipe(
                filter((e) => typeof e.event === 'string'),
                tap((e: TabEvent | DragDropEvent) => {
                    if (e.event === 'dragend') {
                        this._tabset.node.children.forEach((n) =>
                            n.renderContext.element.classList.remove(
                                'cmps-cdk-bg-color-blue-100',
                            ),
                        );
                    }
                    if (e.event === 'drop') {
                        this._store.dispatch(
                            tabsetReorderTab(e.tab.def.id, e.index),
                        );
                    }
                    if (e.event === 'dragenter') {
                        this._tabset.node.children.forEach((n) =>
                            n.renderContext.element.classList.remove(
                                'cmps-cdk-bg-color-blue-100',
                            ),
                        );
                        e.tab.renderContext.element.classList.add(
                            'cmps-cdk-bg-color-blue-100',
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnInit(): void {
        this._storeSub = storeObserver<any, RootState>(this._store, (state) =>
            storeStateMapper(state),
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        const prevState = this.state;
                        this.state = state;
                        this.stateToProps(prevState);
                    }),
                ),
            )
            .subscribe();

        this.opts = {
            orientation: 'horizontal',
            tabs: [],
            defaultRenderFn: null,
        };
    }

    ngAfterViewChecked(): void {
        this._bindTabset();
    }

    ngOnDestroy(): void {
        if (this.tabsetEventsSub) {
            this.tabsetEventsSub.unsubscribe();
        }
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(prevState: RootState): void {
        if (!prevState) {
            this.propsToDispatch();
            return;
        }

        this.opts = { ...this.opts, tabs: this.state.tabs };
        // if (this.tabset) {
        //     this.tabset.node.children.forEach(e => e.renderContext.detectChanges());
        // }

        this.selectedTab = this.state.tabs.find(
            (e) => e.id === this.state.selectedTab,
        );

        this.adjacentTabId = undefined;
        if (this.selectedTab && this.selectedTab.ctx.type !== TabType.ADD) {
            const index = this.state.tabs.indexOf(this.selectedTab);
            if (index !== -1 && index !== 0) {
                this.adjacentTabId = this.state.tabs[index - 1].id;
            }
        }
    }

    propsToDispatch(): void {
        this._store.dispatch(rootInit());
    }

    onSetDefaultTab($event: boolean): void {
        this._store.dispatch(
            tabsetSetTabStateAction(this.selectedTab.id, $event),
        );
    }

    onSetFavouriteTab($event: boolean): void {
        this._store.dispatch(
            tabsetSetTabStateAction(this.selectedTab.id, undefined, $event),
        );
    }

    onAddNewTab($event: Tab<TabContext<InsightTabConfig>>): void {
        this.persistPeriod = true;
        this._store.dispatch(tabsetAddTab($event));
    }

    onTabCloseClick(tab: TabNode): void {
        this.persistPeriod = false;
        this._store.dispatch(tabsetCloseTab(tab.def.id));
    }

    /** @private */
    _onTabClick(
        $event: MouseEvent,
        tab: TabNode,
        overlay: CmpsOverlayTriggerDirective,
    ): void {
        this._currentFocusedTab = tab;

        this._clicks = $event.detail;
        if ($event.detail > 1) {
            this._clicksTimerSub?.unsubscribe();
            this._onOverlayTriggerClick(overlay);
            return;
        }

        this._clicksTimerSub?.unsubscribe();
        this._clicksTimerSub = timer(250)
            .pipe(
                tap(() => {
                    if (this._clicks > 1) {
                        return;
                    }
                    this.persistPeriod = false;
                    this._store.dispatch(tabsetSwitchTab(tab.def.id));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onOverlayTriggerClick(overlay: CmpsOverlayTriggerDirective): void {
        // ViewContainerRef from template is outside NgZone because of experimental.tabset
        (overlay as any)._viewContainerRef = this._viewContainerRef;
        overlay.open();
        this._tabset.node.detectChanges();
    }

    /** @private */
    _onTabRename(value: { id: string; label: string }): void {
        this._store.dispatch(tabsetRenameTab(value.id, value.label));
    }

    /** @private */
    _onSelectUser(): void {
        const ref = this._dialog.open<SearchUserContainerComponent, any, User>(
            SearchUserContainerComponent,
        );
        ref.result
            .pipe(
                tap((user) => {
                    if (user != null) {
                        const tab = createTab(user);
                        this.onAddNewTab(tab);
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bindTabset(): void {
        if (!this._tabset) return;
        if (this.tabsetEventsSub && !this.tabsetEventsSub.closed) return;
        this.tabsetEventsSub = this._tabset.node.events
            .pipe(tap((e) => this._subj.next(e)))
            .subscribe();
    }
}
